<i18n>
{
  "en": {
    "masterProductTitle": "Edit: {0}",
    "consumptionPerPerson": "Consumption per person",
    "consumptionPerPersonHint": "How many units of product does each diner eat",
    "confirm": "Confirm",
    "cancel": "Cancel",
    "confirmTitle": "Are you sure you want to save the changes?",
    "confirmText": "This cannot be undone",
    "invalidNumberError": "Invalid number",
    "validationError": "Please, review all fields",
    "errorInProduct": "Wrong product",
    "emptyFieldError": "Required field",
    "saveProductSuccess": "Product updated successfully",
    "saveProductError": "There was an error saving the product, try again later"
  },
  "es": {
    "masterProductTitle": "Editar: {0}",
    "consumptionPerPerson": "Consumo por persona",
    "consumptionPerPersonHint": "Cuantas unidades del producto principal consume cada comensal",
    "confirm": "Confirmar",
    "cancel": "Cancelar",
    "confirmTitle": "¿Estás seguro de que deseas guardar los cambios?",
    "confirmText": "Esto no se puede deshacer",
    "invalidNumberError": "Por favor, introduce un número válido",
    "validationError": "Por favor, revise todos los campos",
    "errorInProduct": "Product erróneo",
    "emptyFieldError": "Campo obligatorio",
    "saveProductSuccess": "Producto actualizado correctamente",
    "saveProductError": "Hubo un error actualizando el producto, inténtelo de nuevo más tarde."
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <dynameat-confirmation-modal :visible="confirmModalVisible" :title="$t('confirmTitle')"
                                 :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                 @confirm="onConfirmConfirmModal" @cancel="onCloseConfirmModal"/>
    <v-card height="100%" class="px-7 py-5">
      <div class="d-flex align-center justify-space-between mb-2">
        <v-card-title class="word-break pa-0">
          {{ this.$t('masterProductTitle', [title]) }}
        </v-card-title>
      </div>
      <v-container class="pa-0">
        <v-form v-model="valid" class="d-flex flex-column align-left master-product-form form-inputs" ref="form">
                  <v-text-field v-model="consumptionPerPerson" :rules="numberRules" :label="$t('consumptionPerPerson')"
                                :hint="$t('consumptionPerPersonHint')" persistent-hint class="required"/>
          <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm" :confirmText="$t('confirm')"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import { notValidNumber, } from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import DynameatModalHeader from '@dynameat/components/DynameatModalHeader';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import i18n from '@core/plugins/i18n';
import cloneDeep from 'lodash/cloneDeep';
import { DEFAULT_LOCALE } from '@core/constants';

@Component
@Components({ DynameatConfirmationModal, DynameatModalHeader, DynameatModalButtons })
@MapGetters(['masterProductList'])
@MapActions(['addNotification', 'modifyNewProducts', 'removeNotificationById', 'updateMasterProductRequest'])
export default class MasterSubordinateProductModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  @Prop({
    type: Object,
    default: () => ({})
  }) productProp;

  product = null;

  valid = false;

  confirmModalVisible = false;

  consumptionPerPerson = '0.0';

  isNew = false;

  async created() {
    this.mainLanguage = i18n.locale || DEFAULT_LOCALE;
    this.extraLanguage = ['es', 'en'].find((language) => language !== this.mainLanguage);
  }

  @Watch('visible')
  onVisible(val) {
    if (val) {
      this.product = cloneDeep(this.productProp);
      this.loadProductChanges();
      if (this.isNew) {
        this.advancedOptions = true;
      }
      this.$nextTick(() => {
        this.$refs.form?.validate();
      });
    }
  }

  get title() {
    return this.productProp?.name || this.$t('errorInProduct');
  }

  get numberRules() {
    return [notValidNumber(this.$t('invalidNumberError'))];
  }

  applyProductChanges() {
    this.product.consumption_per_person = this.consumptionPerPerson;
    this.product.is_new = false;
    this.product.is_menu_enabled = true;
  }

  loadProductChanges() {
    this.consumptionPerPerson = this.product.consumption_per_person ? String(this.product.consumption_per_person) : String(0.5);
    this.isNew = this.product.is_new;
  }

  onConfirm() {
    this.$refs.form?.validate();
    if (this.valid) {
      this.onOpenConfirmModal();
    } else {
      this.addNotification(createErrorNotification(this.$t('validationError'), { id: 'masterProductValidationError' }));
    }
  }

  onCancel() {
    this.$emit('close');
    this.clearOwnData();
  }

  clearOwnData() {
    this.consumptionPerPerson = '0.0';
    this.isNew = false;
    this.isMenuEnabled = false;
  }

  onOpenConfirmModal() {
    this.confirmModalVisible = true;
  }

  onCloseConfirmModal() {
    this.confirmModalVisible = false;
  }

  async onConfirmConfirmModal() {
    this.confirmModalVisible = false;
    const oldProductIsNew = this.product.is_new;
    this.applyProductChanges();
    if (oldProductIsNew) {
      await this.modifyNewProducts(-1);
    }
    this.$emit('close');
    await this.editSubordinate(this.product);
    this.clearOwnData();
  }

  async editSubordinate(subordinate) {
    try {
      await this.updateMasterProductRequest({ productToUpdate: subordinate, reduced: true });
      this.addNotification(createSuccessNotification(this.$t('saveProductSuccess'), {}));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('saveProductError')));
    } finally {
      this.productToEdit = null;
    }
  }
}
</script>
<style scoped lang="scss">

.form-inputs {
  > div {
    margin-bottom: 15px;
  }
}

.progress-enter-active, .progress-leave-active {
  transition: all 0.3s ease;
}

.progress-enter, .progress-leave-active {
  opacity: 0;
}

.word-break {
  word-break: break-word;
}

.same-gray {
  color: rgba(0, 0, 0, 0.6)
}

.pointer {
  cursor: pointer;
}
</style>

<style>
.master-product-form .v-text-field__details {
  padding-top: 5px;
}

.master-product-form .v-messages__message {
  line-height: 14px;
  font-size: 13px
}

.master-product-form .master-product-input .v-messages {
  min-height: 0;
}

.required label::after {
  content: " *";
  color: red;
}
</style>
